import { cloneDeep, isString } from 'lodash';
import toInteger from 'lodash/toInteger';
import { Editor } from '@/editor';
import { createFontStyle, getBehavior, getFramework } from '@/framework';
import {
    createCanvas,
    createForeignObjectImageUrl,
    cutTransparentArea,
    exportSize,
    fetchVectorText,
    getImgInfoAsync,
    limitMaxImageInfo,
    loadImageAsync,
    loadSvgAsync,
    serializerDomToString,
} from '@/utils/file-kit';
import { formatFontFamilyName, splitStringIntoGroups } from '@/utils/font-kit';
import { computerBgRatio, getImgLayerProps } from '@/utils/layer-kit';
import {
    IBorderRadiusProps,
    IImageAiTextExtension,
    IImageEffectProps,
    IImageLayerProps,
    IImageTransform,
    ILayerProps,
    ImgCategoryType,
    LayerType,
    MaterialIdsType,
    StretchOptionType,
    TextWritingType,
} from '@/version-types';
import { defaultImageTransform, getDefaultImageLayerData } from '../constants/default-image-data';
import ImageEffectAction from './image-effect-action';

/**
 * 绘制圆角
 *
 * @param canvas - Canvas 画布元素对象
 * @param radius - 圆角
 * @param x - 水平坐标
 * @param y - 垂直坐标
 */
export const drawBorderRadius = (canvas: HTMLCanvasElement, radius: IBorderRadiusProps, x: number, y: number) => {
    const { width, height } = canvas;

    const size = width > height ? height / 2 : width / 2;
    const dealRadius = (value: number) => {
        return toInteger(value * 0.01 * size);
    };

    const ctx = canvas.getContext('2d');
    if (ctx) {
        ctx.globalCompositeOperation = 'destination-in';
        ctx.beginPath();
        // 替换 roundRect，使用 arcTo 绘制圆角矩形
        const rt = dealRadius(radius.rightTop);
        const rb = dealRadius(radius.rightBottom);
        const lb = dealRadius(radius.leftBottom);
        const lt = dealRadius(radius.leftTop);

        ctx.moveTo(0, lt);
        // 左上角
        ctx.arcTo(0, 0, lt, 0, lt);
        // 顶边
        ctx.lineTo(width - rt, 0);
        // 右上角
        ctx.arcTo(width, 0, width, rt, rt);
        // 右边
        ctx.lineTo(width, height - rb);
        // 右下角
        ctx.arcTo(width, height, width - rb, height, rb);
        // 底边
        ctx.lineTo(lb, height);
        // 左下角
        ctx.arcTo(0, height, 0, height - lb, lb);
        // 左边
        ctx.lineTo(0, lt);

        ctx.fillStyle = '#000';
        ctx.fill();
        ctx.globalCompositeOperation = 'source-over';
    }
};

/**
 * 设置图层蒙版
 *
 * @param layer - 图层属性
 * @param url - 蒙版图片链接地址
 * @param materialId - 蒙版素材 ID
 * @param identity - 图层 ID
 */
export const setMask = async (layer: ILayerProps, url: string, materialId: string, identity: string) => {
    const data = layer.data as IImageLayerProps;
    data.maskURL = url;

    layer.materialIds = layer.materialIds || {};
    if (url === '') {
        layer.materialIds[MaterialIdsType.MASK] = [];
        layer.shine = false;
        data.stretchOption = StretchOptionType.fill;
    } else {
        layer.materialIds[MaterialIdsType.MASK] = [materialId];
        layer.shine = true;
        data.stretchOption = StretchOptionType.cover;
        // 需要确保初次添加蒙版时，按照原蒙版大小缩放图层、图片
        let maskRatio = 1;
        if (/\.svg$/.test(url)) {
            const svgMaskText = await fetchVectorText(url);
            const { width, height } = exportSize(svgMaskText);
            maskRatio = width / height;
        } else {
            const img = await loadImageAsync(url);
            maskRatio = img.width / img.height;
        }
        const originRatio =
            (layer.width * data.imageTransform.widthRatio) / (layer.height * data.imageTransform.heightRatio);
        if (originRatio > maskRatio) {
            layer.left += Math.ceil((layer.width - layer.height * maskRatio) / 2);
            layer.width = Math.ceil(layer.height * maskRatio);
        } else {
            layer.top += Math.ceil((layer.height - layer.width / maskRatio) / 2);
            layer.height = Math.ceil(layer.width / maskRatio);
        }
        const layerRatio = layer.width / layer.height;
        let widthRatio = 1,
            heightRatio = 1;
        if (originRatio > layerRatio) {
            widthRatio = originRatio / layerRatio;
        } else {
            heightRatio = layerRatio / originRatio;
        }
        const imageTransform = {
            ...data.imageTransform,
            offsetX: 0,
            offsetY: 0,
            widthRatio,
            heightRatio,
        };
        data.imageTransform = imageTransform;
        data.minimumMask = true;
    }

    if (!ImageEffectAction.isEmptyEffect(layer.data as IImageLayerProps)) {
        const layerResultImage = await getBehavior(identity, layer)?.getLayerImageAsync?.(
            layer,
            false,
            false,
            true,
            false,
        );
        if (layerResultImage) {
            const effectUrl = await ImageEffectAction.runEffectAndGetUrl(layer, layerResultImage);
            if (effectUrl && (layer.data as IImageLayerProps).effect) {
                ((layer.data as IImageLayerProps).effect as IImageEffectProps).screenshotUrl = effectUrl as string;
            }
        }
    }
};

export const replaceImage = async (
    layer: ILayerProps,
    url: string,
    stageWidth: number,
    prompt: string,
    materialIds: Record<MaterialIdsType, string[]>,
    imageWidth?: number,
    imageHeight?: number,
    resetSize = false,
) => {
    let newImageWidth = 0;
    let newImageHeight = 0;
    // 如果没有传入图片信息就自己获取
    if (imageWidth === undefined || imageHeight === undefined) {
        // 获取图片信息
        const { width: imageWidth, height: imageHeight } = await getImgInfoAsync(url);
        newImageWidth = imageWidth;
        newImageHeight = imageHeight;
    } else {
        newImageWidth = imageWidth;
        newImageHeight = imageHeight;
    }

    // 如果宽大于1.5倍舞台宽高 就按1.5倍舞台宽高计算
    const limitResult = limitMaxImageInfo(stageWidth, url, newImageWidth, newImageHeight);
    newImageWidth = limitResult.naturalWidth;
    newImageHeight = limitResult.naturalHeight;
    url = limitResult.url;

    let newLayer = cloneDeep(layer);

    if (newLayer.materialIds) {
        newLayer.materialIds[MaterialIdsType.FUNC_LIST] = materialIds[MaterialIdsType.FUNC_LIST] || [];
    }
    newLayer.materialIds = newLayer.materialIds || {};
    newLayer.materialIds[MaterialIdsType.IMAGE_MATERIAL] = materialIds[MaterialIdsType.IMAGE_MATERIAL] || [];
    newLayer.materialIds[MaterialIdsType.PHOTO_MATERIAL] = materialIds[MaterialIdsType.PHOTO_MATERIAL] || [];
    newLayer.materialIds[MaterialIdsType.FILTER] = materialIds[MaterialIdsType.FILTER] || [];

    // 重置尺寸和中心点
    if (resetSize) {
        const layerRatio = newLayer.width / newLayer.height;
        const imageRatio = newImageWidth / newImageHeight;

        if (layerRatio >= imageRatio) {
            const newWidth = newLayer.height * imageRatio;
            newLayer.left = newLayer.left + (newLayer.width - newWidth) / 2;
            newLayer.width = newWidth;
        } else {
            const newHeight = newLayer.width / imageRatio;
            newLayer.top = newLayer.top + (newLayer.height - newHeight) / 2;
            newLayer.height = newHeight;
        }
    }

    const { width: layerWidth, height: layerHeight } = newLayer;
    const imgInfo = newLayer?.data as IImageLayerProps;
    // let { stretchOption } = imgInfo;

    // 每次都要重置imageTransform
    imgInfo.naturalWidth = newImageWidth;
    imgInfo.naturalHeight = newImageHeight;
    imgInfo.url = url;
    imgInfo.cutoutMaskURL = '';
    ImageEffectAction.closeGLRender(newLayer);
    let imageTransform: IImageTransform = {
        ...cloneDeep(defaultImageTransform),
        widthRatio: 1,
        heightRatio: 1,
    };

    // 如果是自由模式还要计算下宽高比
    imageTransform = Object.assign(
        imageTransform,
        computerBgRatio({
            imgWidth: newImageWidth,
            imgHeight: newImageHeight,
            stageHeight: layerHeight,
            stageWidth: layerWidth,
        }),
    );
    imgInfo.imageTransform = imageTransform;

    newLayer = {
        ...newLayer,
        prompt,
        data: {
            ...imgInfo,
        },
    };
    // 海外版替换逻辑处理 用来是否已替换原有主图
    if (window.isIntl && layer.category === ImgCategoryType.mainImg) {
        newLayer.extraInfo = {
            isReplaceMainImage: true,
        };
    }
    return newLayer;
};

/**
 * 创建花字 HTML 预览
 *
 * @param stageSize - 舞台尺寸
 * @param aiText - 花字内容
 * @param layerSize - 图层尺寸
 */
export const createAiTextPreview = (
    aiText: IImageAiTextExtension,
    color?: string,
    background?: string,
    rowTexts = 6,
) => {
    const isVerticalText = aiText.writingMode === TextWritingType.HORIZONTAL_TB;
    const text = aiText.text.replace(/\s+/g, '');
    // const resText = splitStringIntoGroups(text, isVerticalText ? 4 : 6);
    const resText = splitStringIntoGroups(text, rowTexts);
    const textNode = document.createTextNode(resText.join('\n'));
    const fontFamily = aiText.fontFamily;
    const fontSize = 270;
    const divId = `id-${Math.random() + new Date().getTime()}`;
    let html = `<div id="${divId}" style="$wh;padding:0;margin:0;white-space: nowrap; font-size:${fontSize}px;line-height:${
        isVerticalText ? 1.2 : 1.05
    };letter-spacing:${
        isVerticalText ? 10 : 40
    }px;display:flex;align-items:center;justify-content:center;${color ? `color:${color};` : ''}${
        background ? `background:${background};` : ''
    }font-weight:${aiText.bold ? 'bold' : 'normal'};font-style:${
        aiText.italic ? 'italic' : 'normal'
    };writing-mode:${aiText.writingMode};${
        fontFamily ? `font-family:${formatFontFamilyName(fontFamily)};` : ''
    }">${new XMLSerializer().serializeToString(textNode).replace(/\n/g, '<br>')}</div>`;
    const div = document.createElement('div');
    div.style.position = 'absolute';
    div.style.left = '0';
    div.style.top = '0';
    div.style.visibility = 'hidden';
    div.style.pointerEvents = 'none';
    div.innerHTML = html;
    document.body.appendChild(div);
    const { width, height } = document.getElementById(divId)!.getBoundingClientRect();
    const resWidth = Math.floor(width);
    const resHeight = Math.floor(height);
    div.remove();
    html = html.replace('$wh;', `width:${resWidth}px;height:${resHeight}px;`);
    return {
        html,
        width: resWidth,
        height: resHeight,
    };
};

/**
 * 创建花字 SVG 图片
 *
 * @param aiText - AI 花字
 * @param fonts - 字体
 * @param editor - 编辑器实例对象
 * @param color - 可选；颜色
 * @param background - 可选；背景
 * @param rowTexts - 可选；文本行数；默认值为 `6`
 */
export const createAiTextCanvasMask = async (
    aiText: IImageAiTextExtension,
    fonts: any,
    editor: Editor,
    color?: string,
    background?: string,
    rowTexts = 6,
) => {
    const font_name = aiText.fontFamily;
    let fontFamilyStyle = '';
    let fontFamily = '';
    if (font_name) {
        const newTexts = [...new Set(aiText.text)].join('');
        fontFamily = (await editor.font.createTextSubset(newTexts, font_name)) ?? '';
        if (fontFamily) {
            const fontBase64 = editor.font.getSubsetBase64(fontFamily);
            fontFamilyStyle = createFontStyle(fontFamily, fontBase64);
            aiText.fontFamily = fontFamily;
        }
    }
    const { width, height, html } = createAiTextPreview(aiText, color, undefined, rowTexts);
    const style = `<style type="text/css">${fontFamilyStyle}</style>`;
    const div = document.createElement('div');
    div.innerHTML = html;

    const svgWidth = width + 360;
    const svgHeight = height + 360;

    const resHtml = `<div style="display:flex;align-items:center;justify-content:center;width:${svgWidth}px;height:${svgHeight}px;">${serializerDomToString(
        div,
    )}</div>`;
    const imgUrl = createForeignObjectImageUrl(svgWidth, svgHeight, style + resHtml, true);
    const img = await loadSvgAsync(imgUrl);
    const svgImage = await loadSvgAsync(img.src);
    const { canvas: canvasSvg } = createCanvas(svgWidth, svgHeight, svgImage);

    const cutSvg = cutTransparentArea(canvasSvg);

    // console.log('mask cut', canvasSvg.width, canvasSvg.height, cutSvg.width, cutSvg.height);

    const finalWidth = cutSvg.width + 30;
    const finalHeight = cutSvg.height + 30;

    const { canvas, ctx } = createCanvas(finalWidth, finalHeight);
    if (background) {
        ctx.fillStyle = background;
        ctx.fillRect(0, 0, finalWidth, finalHeight);
    }
    ctx.drawImage(cutSvg, 0, 0, cutSvg.width, cutSvg.height, 15, 15, cutSvg.width, cutSvg.height);
    // console.log(width, height, svgWidth, svgHeight, finalWidth, finalHeight);

    return canvas;
};

/**
 * 创建 AI 花字结果画布
 *
 * @param mask - 蒙版元素对象
 * @param imgUrl - 结果图链接地址
 * @param feathering - 可选；是否羽化；默认值为 `false`
 */
export const createAiTextCanvas = async (mask: HTMLCanvasElement | string, imgUrl: string, feathering = false) => {
    const resImg = await loadImageAsync(imgUrl, false);
    if (isString(mask)) {
        const maskImg = await loadImageAsync(mask, false);
        mask = createCanvas(maskImg.width, maskImg.height, maskImg).canvas;
    }
    const { canvas: maskCanvas, ctx } = createCanvas(mask.width, mask.height, mask);
    if (!feathering) {
        const { canvas: resCanvas } = createCanvas(resImg.width, resImg.height, resImg);
        ctx.globalCompositeOperation = 'source-in';
        ctx.drawImage(resCanvas, 0, 0);
        return cutTransparentArea(maskCanvas);
    } else {
        // // TODO: 抠图相关
        // const mattingResult = await getImageMattingResult({
        //     width: resImg.width,
        //     height: resImg.height,
        //     image: resImg,
        //     maskImage: maskCanvas,
        //     useBlurFilterRun: true,
        // });
        // if (mattingResult) {
        //     return cutTransparentArea(createCanvas(resImg.width, resImg.height, mattingResult).canvas);
        // }
        return null;
    }
};

export const getFlipStyle = (flipH: boolean, flipV: boolean) => {
    const flipStyle: React.CSSProperties = {
        pointerEvents: 'none',
    };

    if (flipH) {
        flipStyle.transform = 'scaleX(-1) ';
    }
    if (flipV) {
        const ry180 = 'scaleY(-1)';
        flipStyle.transform = flipStyle.transform ? flipStyle.transform + ry180 : ry180;
    }

    flipStyle.width = '100%';
    flipStyle.height = '100%';
    return flipStyle;
};

export const getInnerStyle = (
    originImgWidth: number,
    originImgHeight: number,
    offsetX: number,
    offsetY: number,
    rotate: number,
    scale: number,
    maskURL: string,
    layerWidth: number,
    layerHeight: number,
) => {
    const style = {
        width: `${originImgWidth}px`,
        height: `${originImgHeight}px`,
        transform: `translate(${offsetX + (layerWidth - originImgWidth) / 2}px, ${offsetY + (layerHeight - originImgHeight) / 2}px) rotate(${rotate}deg) scale(${+scale.toFixed(5)})`,
        transformOrigin: 'center center',
        ...(maskURL && { WebkitMaskClip: 'content-box' }),
    };

    return style;
};

export const getImageTransformInfo = (layer: ILayerProps) => {
    const { width, height } = layer;
    const {
        imageTransform: { widthRatio, heightRatio, offsetX: offsetXRatio, offsetY: offsetYRatio, scale },
    } = layer.data as IImageLayerProps;

    const originImgWidth = width * widthRatio;
    const originImgHeight = height * heightRatio;
    const offsetX = offsetXRatio * originImgWidth * scale;
    const offsetY = offsetYRatio * originImgHeight * scale;

    return { offsetX, offsetY, originImgWidth, originImgHeight };
};

export const getImageStyle = (originImgWidth: number, originImgHeight: number) => {
    return {
        width: `${originImgWidth}px`,
        height: `${originImgHeight}px`,
        pointerEvents: 'none',
        display: 'block',
    } as React.CSSProperties;
};

export const getImageMaskStyle = (maskURL: string, minimumMask: boolean | undefined) => {
    const imageWrapStyle = {} as React.CSSProperties;

    imageWrapStyle.WebkitMaskImage = `url(${maskURL})`;
    imageWrapStyle.WebkitMaskSize = minimumMask ? '100% 100%' : 'contain';
    imageWrapStyle.WebkitMaskRepeat = 'no-repeat';
    imageWrapStyle.WebkitMaskPosition = 'center';
    imageWrapStyle.transform = 'scale(1)';
    imageWrapStyle.overflow = 'hidden';

    return imageWrapStyle;
};

/**
 * 执行图片特效
 *
 * @param identity - 图层标识
 * @param layerId - 图层 ID
 * @returns 是否执行成功
 */
export const doImageEffect = async (identity: string, layerId: string) => {
    try {
        const framework = getFramework(identity);
        const layer = framework.getLayer(layerId, true);
        if (layer.layerType !== LayerType.IMAGE_LAYER) {
            return false;
        }
        if (ImageEffectAction.isEmptyEffect(layer.data as IImageLayerProps)) {
            return false;
        }

        const { effect } = layer.data as IImageLayerProps;
        if (effect) {
            effect.screenshotUrl = '';
            framework.stage.addOrUpdate(cloneDeep(layer));
        }

        const layerResultImage = await getBehavior(identity, layer)?.getLayerImageAsync?.(
            layer,
            false,
            false,
            true,
            false,
        );
        if (layerResultImage) {
            await ImageEffectAction.glRender(layer, layerResultImage);
            const url = await ImageEffectAction.getScreenshotUrl(layer);
            if (effect && url) {
                effect.screenshotUrl = url;
                framework.stage.addOrUpdate(layer);
                return true;
            }
        }
    } catch (error) {
        console.error(error);
        return false;
    }
    return false;
};

export const createImageLayer = (
    url: string,
    width: number,
    height: number,
    options?: {
        left?: number;
        top?: number;
        materialId?: string;
        naturalWidth?: number;
        naturalHeight?: number;
        isPhoto?: boolean;
    },
) => {
    const { left, top, materialId, naturalWidth, naturalHeight, isPhoto } = options || {};
    // 获取图片图层默认数据
    const imageLayerData = getDefaultImageLayerData(
        url,
        width,
        height,
        left ?? 0,
        top ?? 0,
        materialId,
        naturalWidth,
        naturalHeight,
        isPhoto,
    );

    // 获取图片图层数据
    const imageLayer = getImgLayerProps(imageLayerData);

    return imageLayer;
};
